import {
  type PersonContactInfoResponse,
  type PlayerProfileResponse,
} from '@on3/api';
import { Link } from '@on3/ui-lib/components/Link';
import { SocialIcon } from '@on3/ui-lib/components/SocialIcon';
import { Pen } from '@on3/ui-lib/components/Svg';
import { useAuth } from '@on3/ui-lib/index';
import { formatPhoneNumber } from '@on3/ui-lib/utils/formatters';
import clsx from 'clsx';
import { useModal } from 'components/Modals';
import { useCallback } from 'react';
import { socialLink } from 'utils/social';

import { ContactsMock } from './Mock';
import styles from './PlayerContact.module.scss';

interface PlayerContactProps {
  player: PlayerProfileResponse;
  contactInfo?: PersonContactInfoResponse | null;
}

export const PlayerContact = ({ player, contactInfo }: PlayerContactProps) => {
  const { showModal } = useModal();
  const { hasRdbAccess, isVerified } = useAuth();

  const filterInfo = useCallback(
    (info?: string | null) => {
      if (hasRdbAccess()) {
        return info;
      }

      return 'Private';
    },
    [hasRdbAccess],
  );

  const handleEdit = () => {
    if (!contactInfo) return;
    showModal('player.contact', {
      player,
      contactInfo,
    });
  };

  const canEdit = hasRdbAccess();
  const canEditRecruit = isVerified(player.key);

  return (
    <section className={clsx(styles.block)}>
      <header>
        <h2>Contacts</h2>
        {canEdit ? (
          <button className={styles.edit} onClick={handleEdit} type="button">
            <Pen />
            Edit
          </button>
        ) : canEditRecruit ? (
          <Link
            className={styles.edit}
            forceReload
            href={`/rivals/${player.slug}/elite/network/settings/contacts/`}
          >
            <Pen />
            Edit
          </Link>
        ) : null}
      </header>
      {contactInfo ? (
        <ul className={styles.list}>
          <ul>
            <li data-has={!!contactInfo.email?.value}>
              <span>Email</span>
              <span>{filterInfo(contactInfo?.email?.value) || '-'}</span>
            </li>
            <li data-has={!!contactInfo.phoneNumber?.value}>
              <span>Phone</span>
              <span>
                {filterInfo(
                  formatPhoneNumber(contactInfo?.phoneNumber?.value as string),
                ) || '-'}
              </span>
            </li>
            <li data-has={!!contactInfo.instagramHandle?.value}>
              <span>Instagram</span>
              <Link
                className={styles.hasIcon}
                href={socialLink(
                  'instagram',
                  contactInfo?.instagramHandle?.value,
                )}
              >
                <SocialIcon type="instagram" />
                {contactInfo?.instagramHandle?.value || '-'}
              </Link>
            </li>
            <li data-has={!!contactInfo.twitterHandle?.value}>
              <span>X</span>
              <Link
                className={styles.hasIcon}
                href={socialLink('twitter', contactInfo?.twitterHandle?.value)}
              >
                <SocialIcon type="x" />
                {contactInfo?.twitterHandle?.value || '-'}
              </Link>
            </li>
            <li data-has={!!contactInfo.tikTokHandle?.value}>
              <span>TikTok</span>
              <Link
                className={styles.hasIcon}
                href={socialLink('tiktok', contactInfo?.tikTokHandle?.value)}
              >
                <SocialIcon type="tiktok" />
                {contactInfo?.tikTokHandle?.value || '-'}
              </Link>
            </li>
          </ul>
          {hasRdbAccess() && (
            <>
              <ul>
                <li data-has={!!contactInfo.parentInfo?.value?.name}>
                  <span>Guardian Name</span>
                  <span>{contactInfo?.parentInfo?.value?.name || '-'}</span>
                </li>
                <li data-has={!!contactInfo.parentInfo?.value?.email}>
                  <span>Guardian Email</span>
                  <span>{contactInfo?.parentInfo?.value?.email || '-'}</span>
                </li>
                <li data-has={!!contactInfo.parentInfo?.value?.phoneNumber}>
                  <span>Guardian Phone</span>
                  <span>
                    {formatPhoneNumber(
                      contactInfo?.parentInfo?.value?.phoneNumber as string,
                    ) || '-'}
                  </span>
                </li>
              </ul>
              <ul>
                <li data-has={!!contactInfo.agentInfo?.value?.name}>
                  <span>Agent Name</span>
                  <span>{contactInfo?.agentInfo?.value?.name || '-'}</span>
                </li>
                <li data-has={!!contactInfo.agentInfo?.value?.agencyName}>
                  <span>Agency</span>
                  <span>
                    {contactInfo?.agentInfo?.value?.agencyName || '-'}
                  </span>
                </li>
                <li data-has={!!contactInfo.agentInfo?.value?.email}>
                  <span>Agent Email</span>
                  <span>{contactInfo?.agentInfo?.value?.email || '-'}</span>
                </li>
                <li data-has={!!contactInfo.agentInfo?.value?.phoneNumber}>
                  <span>Agent Phone</span>
                  <span>
                    {formatPhoneNumber(
                      contactInfo?.agentInfo?.value?.phoneNumber as string,
                    ) || '-'}
                  </span>
                </li>
              </ul>
            </>
          )}
        </ul>
      ) : (
        <ContactsMock />
      )}
      <div className={styles.disclaimer}>
        <p>Contact information shared with Coaches and NIL Brands only.</p>
      </div>
    </section>
  );
};
